/**
 * Pure Regeln der Unterlagen-Checkliste (Reiter „Unterlagen" der
 * Planungsphase).
 *
 * Die Checkliste fuehrt vier Quellen zusammen:
 *   1. den Katalog mitgeltender Dokumente (`src/services/mitgeltendeDokumente`,
 *      nur Art A und B — Art C steht einmal als Grundsatz, nie je Zeile),
 *   2. die Projektmerkmale (dreiwertig: `true` → erforderlich,
 *      `undefined` → zu klaeren, nur `false` schliesst aus),
 *   3. die mitgeltenden Dokumente der Arbeitsschritte des aktuellen SiGe-Plans,
 *   4. den gespeicherten Stand (`planungsphasen.unterlagen`).
 *
 * Alles, was sich ohne DOM und ohne Supabase entscheiden laesst, steht hier —
 * es gibt kein jsdom, Komponenten sind nicht testbar (stehende Entscheidung
 * „duenner Container, Logik in Hooks und puren Funktionen").
 *
 * ═══ DREI REGELN, DIE HIER FESTGENAGELT SIND ═════════════════════════════
 *
 * (a) NIE AUTOMATISCH ENTFERNEN. Ein gespeicherter Eintrag bleibt sichtbar,
 *     auch wenn das Merkmal zurueckgenommen, der SiGe-Plan geaendert oder das
 *     Dokument aus dem Katalog gestrichen wurde. Mit ihm verschwaende sonst
 *     der Nachweis, dass es angefordert wurde.
 *
 * (b) DER ERSTE STEMPEL ZAEHLT. `angefordert_am` wird beim ersten Wechsel auf
 *     „angefordert" gesetzt und danach nie ueberschrieben — wie `gestellt_am`
 *     bei den Fragen: im Streitfall zaehlt, wann zum ersten Mal angefordert
 *     wurde, nicht wann zuletzt erinnert.
 *
 * (c) „NICHT ERFORDERLICH" IST EINE ENTSCHEIDUNG. Wer eine Unterlage so
 *     einstuft, muss begruenden — und die Begruendung laesst sich danach nicht
 *     leeren, solange der Status gilt. Eine unbegruendete Ausbuchung waere im
 *     Haftungsfall wertlos.
 */

import {
    DOKUMENTE,
    GRUPPEN_TITEL,
    dokumenteFuerMerkmale,
    findeDokument,
    normalisiere,
} from '../../services/mitgeltendeDokumente';
import type {
    DokumentGruppe,
    MerkmalTreffer,
    MitgeltendesDokument,
} from '../../services/mitgeltendeDokumente';
import { MERKMAL_LABEL } from './fragenkatalog';
import { fristDatum } from './planungLogic';
import type {
    FrageAdressat,
    PlanungUnterlage,
    ProjektMerkmale,
    SigeSchrittDokumente,
    UnterlagenQuelle,
    UnterlagenStatus,
} from './types';

// ═══════════════════════════════════════════════════════════════════════════
// BESCHRIFTUNGEN
// ═══════════════════════════════════════════════════════════════════════════

export const UNTERLAGEN_STATUS_LABEL: Record<UnterlagenStatus, string> = {
    offen: 'Offen',
    angefordert: 'Angefordert',
    liegt_vor: 'Liegt vor',
    nicht_erforderlich: 'Nicht erforderlich',
};

export const UNTERLAGEN_STATUS_WERTE: UnterlagenStatus[] = ['offen', 'angefordert', 'liegt_vor', 'nicht_erforderlich'];

/** Kurzform der Dokumentart fuer die Zeile. Die Langform steht in `ART_TITEL`. */
export const ART_KURZ: Record<'A' | 'B', string> = {
    A: 'Planer/Bauherr',
    B: 'Ausführender vor Beginn',
};

export const BELEG_LABEL: Record<'MK' | 'praxis', string> = {
    MK: 'belegt (Mängelkatalog)',
    praxis: 'Fachpraxis',
};

/**
 * Merkmale, die `MERKMAL_LABEL` (Erfassungsmaske der Grundlagen) nicht
 * fuehrt, weil sie dort als Zahl oder in einem eigenen Block stehen. Der
 * Katalog darf laut Vertrag nur boolesche Merkmale nennen — die Ergaenzung
 * fängt den einen booleschen Fall ab, der nicht in der Tabelle steht.
 */
const ERGAENZENDE_MERKMAL_LABEL: Record<string, string> = {
    mehrereArbeitgeber: 'Beschäftigte mehrerer Arbeitgeber',
};

/** Klartext eines Merkmals — dieselbe Beschriftung wie in den Grundlagen. */
export const merkmalText = (k: keyof ProjektMerkmale | string): string =>
    MERKMAL_LABEL[String(k)] || ERGAENZENDE_MERKMAL_LABEL[String(k)] || String(k);

/** ISO-Zeitpunkt → TT.MM.JJJJ. Leer bei unbrauchbarer Eingabe. */
export const datumDeutsch = (iso?: string | null): string => {
    const t = String(iso || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
    const [j, m, d] = t.split('-');
    return `${d}.${m}.${j}`;
};

// ═══════════════════════════════════════════════════════════════════════════
// RUECKMELDEFRIST — `frist_am` am Eintrag (ISO-Tag, nur Anzeige)
// ═══════════════════════════════════════════════════════════════════════════

/** Vorgabefrist beim Anfordern in Tagen — dieselbe Spanne wie die A-Fragen
 *  des Katalogs (`fristTage`, `fragenkatalog.ts`). */
export const UNTERLAGEN_FRIST_TAGE = 14;

/** Ein ISO-Tag (YYYY-MM-DD) oder `null`. Zeitanteile werden abgeschnitten,
 *  Unbrauchbares faellt weg — ein kaputtes Datum ist keine Frist. */
const isoTagOderNull = (v: unknown): string | null => {
    const t = typeof v === 'string' ? v.slice(0, 10) : '';
    return /^\d{4}-\d{2}-\d{2}$/.test(t) && isFinite(Date.parse(`${t}T00:00:00Z`)) ? t : null;
};

/** Vorgabe „heute + 14 Tage". `heuteIso` ist der Tag in ORTSZEIT
 *  (`heuteLokalIso` in `planungLogic.ts`), nicht `toISOString()` — der liefert
 *  nachts den Vortag. Leer bei unbrauchbarem Tag. */
export const vorgabeUnterlagenFrist = (heuteIso: string): string =>
    fristDatum(heuteIso, UNTERLAGEN_FRIST_TAGE);

/** Frist verstrichen und die Unterlage noch unerledigt. ISO-Tage sind
 *  lexikografisch vergleichbar — kein Date-Parsing. Der Fristtag selbst ist
 *  noch nicht ueberfaellig (wie bei den Fragen). */
export const unterlagenFristUeberfaellig = (e: PlanungUnterlage | null | undefined, heuteIso: string): boolean => {
    if (!e || !e.frist_am) return false;
    if (!(e.status === 'offen' || e.status === 'angefordert')) return false;
    return e.frist_am.slice(0, 10) < String(heuteIso || '').slice(0, 10);
};

/** „Frist 31.10.2026" bzw. „Frist abgelaufen am 31.10.2026" — leer ohne
 *  Frist oder bei erledigter Unterlage. */
export const unterlagenFristText = (e: PlanungUnterlage | null | undefined, heuteIso: string): string => {
    if (!e || !e.frist_am) return '';
    if (!(e.status === 'offen' || e.status === 'angefordert')) return '';
    const tag = datumDeutsch(e.frist_am);
    if (!tag) return '';
    return unterlagenFristUeberfaellig(e, heuteIso) ? `Frist abgelaufen am ${tag}` : `Frist ${tag}`;
};

// ═══════════════════════════════════════════════════════════════════════════
// GESPEICHERTER STAND LESEN
// ═══════════════════════════════════════════════════════════════════════════

const STATUS_MENGE = new Set<string>(UNTERLAGEN_STATUS_WERTE);
const QUELLEN = new Set<string>(['katalog', 'sigeplan', 'eigen']);
const ADRESSATEN = new Set<string>(['BH', 'OP', 'TWP', 'TGA', 'FP', 'GUT', 'BEH', 'AN', 'BET']);

/** Praefixe der Kennungen freier Eintraege. Katalogeintraege tragen die
 *  Katalog-Id ohne Praefix — die enthaelt keinen Doppelpunkt. */
const PRAEFIX_EIGEN = 'eigen';
const PRAEFIX_PLAN = 'plan';
const TRENNER = ':';

const text = (v: unknown): string => (typeof v === 'string' ? v : '');
const textOderNull = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

/**
 * Macht aus dem Inhalt der jsonb-Spalte eine saubere Liste.
 *
 * Fehlt die Spalte (Migration Nr. 156 noch nicht gefahren) oder steht etwas
 * anderes als ein Array darin, gibt es `[]` — Lesen ist fail-open wie im
 * ganzen Modul. Einzelne Eintraege werden NICHT verworfen, solange sie eine
 * Kennung haben: ein unbekannter Status wird zu „offen", statt den Eintrag
 * samt Anforderungsdatum zu verlieren (Regel (a)). Nur Eintraege ohne
 * Kennung fallen weg — sie lassen sich keiner Zeile zuordnen. Doppelte
 * Kennungen: der erste gewinnt, damit die Liste eindeutig bleibt.
 */
export const normalisiereUnterlagen = (roh: unknown): PlanungUnterlage[] => {
    if (!Array.isArray(roh)) return [];
    const gesehen = new Set<string>();
    const out: PlanungUnterlage[] = [];
    roh.forEach(e => {
        if (!e || typeof e !== 'object' || Array.isArray(e)) return;
        const r = e as Record<string, unknown>;
        const id = text(r.id).trim();
        if (!id || gesehen.has(id)) return;
        gesehen.add(id);
        const quelle = QUELLEN.has(text(r.quelle)) ? (r.quelle as UnterlagenQuelle) : quelleAusId(id);
        // `frist_am` nur, wenn eine brauchbare Frist gespeichert ist: der
        // Altbestand traegt das Feld nicht, und er soll beim Lesen nicht
        // stillschweigend eine andere Gestalt bekommen.
        const fristAm = isoTagOderNull(r.frist_am);
        out.push({
            id,
            quelle,
            name: text(r.name).trim() || id,
            status: STATUS_MENGE.has(text(r.status)) ? (r.status as UnterlagenStatus) : 'offen',
            bemerkung: text(r.bemerkung),
            angefordert_am: textOderNull(r.angefordert_am),
            status_am: textOderNull(r.status_am),
            ...(fristAm ? { frist_am: fristAm } : {}),
            dokument_id: textOderNull(r.dokument_id),
            lieferant: Array.isArray(r.lieferant)
                ? (r.lieferant as unknown[]).filter((a): a is FrageAdressat => typeof a === 'string' && ADRESSATEN.has(a))
                : [],
        });
    });
    return out;
};

const quelleAusId = (id: string): UnterlagenQuelle => {
    if (id.startsWith(`${PRAEFIX_EIGEN}${TRENNER}`)) return 'eigen';
    if (id.startsWith(`${PRAEFIX_PLAN}${TRENNER}`)) return 'sigeplan';
    return 'katalog';
};

// ═══════════════════════════════════════════════════════════════════════════
// SIGE-PLAN — welche Dokumente nennt der aktuelle Plan?
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Stabiler Schluessel eines freien Plan-Eintrags.
 *
 * Bewusst NICHT `normalisiere` aus dem Katalog: dessen Umschrift darf sich
 * mit dem Katalog weiterentwickeln, die Kennung eines gespeicherten
 * Eintrags aber nicht — sonst fiele der gespeicherte Status nach einem
 * Katalog-Update von seiner Planzeile ab. Hier nur Kleinschreibung und
 * zusammengezogene Leerzeichen; das aendert sich nicht.
 */
export const planSchluessel = (name: string): string =>
    `${PRAEFIX_PLAN}${TRENNER}${String(name || '').toLowerCase().replace(/\s+/g, ' ').trim()}`;

/**
 * Sucht ein Katalogdokument (jeder Art) zu einem Namen.
 *
 * Erst `findeDokument` (der Vertrag: Id, Name oder Alias, normalisiert),
 * dann ein eigener Abgleich ueber Id, Name, Kurzform und Alias gegen den
 * uebergebenen Katalog. Der zweite Weg ist kein Misstrauen gegen den
 * Vertrag, sondern deckt die Kurzform ab (Kuerzel am Schritt statt der
 * Langform) und haelt die Funktion gegen einen Testkatalog pruefbar.
 */
const sucheImKatalog = (
    name: string,
    katalog: readonly MitgeltendesDokument[],
): MitgeltendesDokument | undefined => {
    const n = normalisiere(name);
    if (!n) return undefined;
    const ueberVertrag = findeDokument(name);
    if (ueberVertrag) {
        const imKatalog = katalog.find(d => d.id === ueberVertrag.id);
        if (imKatalog) return imKatalog;
    }
    return katalog.find(d =>
        normalisiere(d.id) === n
        || normalisiere(d.name) === n
        || (!!d.kurz && normalisiere(d.kurz) === n)
        || (d.alias || []).some(a => normalisiere(a) === n));
};

/** Wie `sucheImKatalog`, aber Art C zaehlt nie als Treffer — sie steht im
 *  Grundsatz, nicht je Zeile. */
export const katalogDokumentZuName = (
    name: string,
    katalog: readonly MitgeltendesDokument[],
): MitgeltendesDokument | undefined => {
    const d = sucheImKatalog(name, katalog);
    return d && d.art !== 'C' ? d : undefined;
};

export interface SigeZuordnung {
    /** Katalog-Id → Bezeichnungen der Arbeitsschritte, die das Dokument nennen. */
    katalog: Map<string, string[]>;
    /** Freie Plan-Eintraege ohne Katalogtreffer, in Planreihenfolge. */
    frei: { id: string; name: string; schritte: string[] }[];
}

const OHNE_BEZEICHNUNG = 'Arbeitsschritt ohne Bezeichnung';

const merkeSchritt = (liste: string[], schritt: string) => {
    if (!liste.includes(schritt)) liste.push(schritt);
};

/** Ordnet die Dokumentnamen der Arbeitsschritte dem Katalog zu. */
export const ordneSigeDokumenteZu = (
    schritte: readonly SigeSchrittDokumente[],
    katalog: readonly MitgeltendesDokument[],
): SigeZuordnung => {
    const kat = new Map<string, string[]>();
    const frei = new Map<string, { id: string; name: string; schritte: string[] }>();
    (schritte || []).forEach(s => {
        if (!s) return;
        const schritt = String(s.bezeichnung || '').trim() || OHNE_BEZEICHNUNG;
        (s.dokumente || []).forEach(rohName => {
            const name = String(rohName || '').trim();
            if (!name) return;
            const dok = sucheImKatalog(name, katalog);
            // Ein Art-C-Dokument im Plan ist kein freier Eintrag: es ist
            // durch den Grundsatz abgedeckt und soll nicht als „unbekannte
            // Unterlage" in der Checkliste auftauchen.
            if (dok && dok.art === 'C') return;
            if (dok) {
                const liste = kat.get(dok.id) || [];
                merkeSchritt(liste, schritt);
                kat.set(dok.id, liste);
                return;
            }
            // Zusammengefasst wird ueber den normalisierten Namen, damit
            // „Prüfbericht" und „Pruefbericht" EINE Zeile ergeben; die
            // Kennung kommt von der ersten Schreibweise.
            const schluessel = normalisiere(name);
            const eintrag = frei.get(schluessel) || { id: planSchluessel(name), name, schritte: [] };
            merkeSchritt(eintrag.schritte, schritt);
            frei.set(schluessel, eintrag);
        });
    });
    return { katalog: kat, frei: Array.from(frei.values()) };
};

// ═══════════════════════════════════════════════════════════════════════════
// ZEILEN DER CHECKLISTE
// ═══════════════════════════════════════════════════════════════════════════

/** Relevanz aus den Merkmalen: `null` = kein Merkmal spricht dafuer. */
export type MerkmalRelevanz = 'erforderlich' | 'zu_klaeren' | null;

/** Gruppe einer Zeile: die Katalog-Gruppe oder einer der drei Sondertoepfe. */
export type ZeilenGruppe = DokumentGruppe | 'sigeplan' | 'eigen' | 'archiv';

export interface UnterlagenZeile {
    /** Schluessel = `PlanungUnterlage.id` des (kuenftigen) Eintrags. */
    id: string;
    quelle: UnterlagenQuelle;
    name: string;
    gruppe: ZeilenGruppe;
    /** Katalogdokument, sofern es (noch) im Katalog als Art A/B steht. */
    dokument: MitgeltendesDokument | null;
    /** Gespeicherter Eintrag, sofern die Zeile schon angefasst wurde. */
    eintrag: PlanungUnterlage | null;
    status: UnterlagenStatus;
    relevanz: MerkmalRelevanz;
    /** Die ausloesenden Merkmale zu `relevanz`. */
    merkmale: (keyof ProjektMerkmale)[];
    /** Arbeitsschritte des aktuellen SiGe-Plans, die das Dokument nennen. */
    sigeSchritte: string[];
    /** Merkmal ausdruecklich wahr ODER im SiGe-Plan genannt. */
    erforderlich: boolean;
    /** Erscheint im Standardfilter „Relevant". */
    relevant: boolean;
    /** Wer liefert (Katalog bzw. eigener Eintrag). */
    lieferant: FrageAdressat[];
}

export interface ChecklistenEingang {
    merkmale: ProjektMerkmale;
    eintraege: readonly PlanungUnterlage[];
    sigeSchritte: readonly SigeSchrittDokumente[];
    /** Nur fuer Tests: anderer Katalog bzw. andere Merkmalsauswertung. */
    katalog?: readonly MitgeltendesDokument[];
    merkmalTreffer?: readonly MerkmalTreffer[];
}

const statusVon = (e: PlanungUnterlage | null): UnterlagenStatus => (e ? e.status : 'offen');

/**
 * Baut die Zeilen der Checkliste.
 *
 * Reihenfolge: alle Katalogdokumente der Art A und B in Katalogreihenfolge,
 * danach freie Eintraege aus dem SiGe-Plan, eigene Eintraege und zuletzt
 * gespeicherte Katalogeintraege, die der Katalog nicht mehr fuehrt
 * (Regel (a): sie verschwinden nicht, sie wandern in den Topf „archiv").
 */
export const baueUnterlagenZeilen = (eingang: ChecklistenEingang): UnterlagenZeile[] => {
    const katalog = eingang.katalog || DOKUMENTE;
    const treffer = eingang.merkmalTreffer || dokumenteFuerMerkmale(eingang.merkmale || {});
    const trefferJeId = new Map<string, MerkmalTreffer>();
    (treffer || []).forEach(t => { if (t && t.dokument) trefferJeId.set(t.dokument.id, t); });

    const eintraege = normalisiereUnterlagen(eingang.eintraege || []);
    const eintragJeId = new Map(eintraege.map(e => [e.id, e]));
    const verbraucht = new Set<string>();
    const sige = ordneSigeDokumenteZu(eingang.sigeSchritte || [], katalog);

    const zeilen: UnterlagenZeile[] = [];

    katalog.filter(d => d.art === 'A' || d.art === 'B').forEach(d => {
        const eintrag = eintragJeId.get(d.id) || null;
        if (eintrag) verbraucht.add(d.id);
        const t = trefferJeId.get(d.id);
        const relevanz: MerkmalRelevanz = t ? t.stand : null;
        const sigeSchritte = sige.katalog.get(d.id) || [];
        zeilen.push({
            id: d.id,
            quelle: 'katalog',
            name: d.name,
            gruppe: d.gruppe,
            dokument: d,
            eintrag,
            status: statusVon(eintrag),
            relevanz,
            merkmale: t ? [...t.merkmale] : [],
            sigeSchritte,
            erforderlich: relevanz === 'erforderlich' || sigeSchritte.length > 0,
            relevant: relevanz !== null || sigeSchritte.length > 0 || !!eintrag,
            lieferant: [...(d.lieferant || [])],
        });
    });

    // Freie Plan-Treffer: erst ueber die Kennung, dann ueber den
    // normalisierten Namen einem gespeicherten eigenen oder Plan-Eintrag
    // zuordnen. Sonst stuende eine eigene Unterlage „liegt vor" neben einer
    // offenen Planzeile desselben Namens — dieselbe Unterlage zweimal, mit
    // widerspruechlichem Fortschritt und doppelter Anforderung. Der
    // gespeicherte Eintrag behaelt Status, Beleg und Stempel; die Zeile
    // zeigt zusaetzlich „im SiGe-Plan". Die Kennung allein reicht nicht:
    // `planSchluessel` und `normalisiere` schreiben verschieden um
    // („Prüfbericht" ≠ „Pruefbericht" im Schluessel, gleich im Namen).
    const zeileZuEintrag = new Map<string, UnterlagenZeile>();
    sige.frei.forEach(f => {
        const n = normalisiere(f.name);
        const eintrag = eintragJeId.get(f.id)
            || eintraege.find(e => (e.quelle === 'eigen' || e.quelle === 'sigeplan') && normalisiere(e.name) === n)
            || null;
        if (eintrag) {
            const schon = zeileZuEintrag.get(eintrag.id);
            if (schon) {
                f.schritte.forEach(s => merkeSchritt(schon.sigeSchritte, s));
                return;
            }
            verbraucht.add(eintrag.id);
        }
        const z: UnterlagenZeile = {
            id: eintrag ? eintrag.id : f.id,
            quelle: eintrag ? eintrag.quelle : 'sigeplan',
            name: eintrag ? eintrag.name : f.name,
            gruppe: eintrag && eintrag.quelle === 'eigen' ? 'eigen' : 'sigeplan',
            dokument: null,
            eintrag,
            status: statusVon(eintrag),
            relevanz: null,
            merkmale: [],
            sigeSchritte: [...f.schritte],
            erforderlich: true,
            relevant: true,
            lieferant: eintrag ? [...eintrag.lieferant] : [],
        };
        if (eintrag) zeileZuEintrag.set(eintrag.id, z);
        zeilen.push(z);
    });

    // Gespeichertes, das keiner Katalog- oder Planzeile mehr gehoert.
    eintraege.filter(e => !verbraucht.has(e.id)).forEach(e => {
        zeilen.push({
            id: e.id,
            quelle: e.quelle,
            name: e.name,
            gruppe: e.quelle === 'eigen' ? 'eigen' : e.quelle === 'sigeplan' ? 'sigeplan' : 'archiv',
            dokument: null,
            eintrag: e,
            status: e.status,
            relevanz: null,
            merkmale: [],
            sigeSchritte: [],
            erforderlich: false,
            relevant: true,
            lieferant: [...e.lieferant],
        });
    });

    return zeilen;
};

/** Titel der Sondertoepfe; die Katalog-Gruppen kommen aus `GRUPPEN_TITEL`. */
export const SONDER_GRUPPEN_TITEL: Record<'sigeplan' | 'eigen' | 'archiv', string> = {
    sigeplan: 'Aus dem SiGe-Plan (nicht im Katalog)',
    eigen: 'Eigene Unterlagen',
    archiv: 'Nicht mehr im Katalog',
};

const GRUPPEN_ORDNUNG: ZeilenGruppe[] = [
    ...(Object.keys(GRUPPEN_TITEL) as DokumentGruppe[]).filter(g => g !== 'allgemein'),
    'sigeplan', 'eigen', 'archiv',
];

export const gruppenTitel = (g: ZeilenGruppe): string =>
    g === 'sigeplan' || g === 'eigen' || g === 'archiv'
        ? SONDER_GRUPPEN_TITEL[g]
        : GRUPPEN_TITEL[g] || String(g);

/** Gruppiert in fester Reihenfolge; leere Gruppen fallen weg. */
export const gruppiereUnterlagen = (
    zeilen: readonly UnterlagenZeile[],
): { gruppe: ZeilenGruppe; titel: string; zeilen: UnterlagenZeile[] }[] => {
    const map = new Map<ZeilenGruppe, UnterlagenZeile[]>();
    (zeilen || []).forEach(z => {
        const liste = map.get(z.gruppe) || [];
        liste.push(z);
        map.set(z.gruppe, liste);
    });
    const bekannt = GRUPPEN_ORDNUNG.filter(g => map.has(g));
    // Eine Gruppe, die erst ein spaeterer Katalog kennt, haengt hinten an
    // statt zu verschwinden.
    const unbekannt = Array.from(map.keys()).filter(g => !GRUPPEN_ORDNUNG.includes(g));
    return [...bekannt, ...unbekannt].map(g => ({ gruppe: g, titel: gruppenTitel(g), zeilen: map.get(g) || [] }));
};

/** Die Art-C-Dokumente des Katalogs fuer den Grundsatz-Kasten. */
export const artCDokumente = (
    katalog: readonly MitgeltendesDokument[] = DOKUMENTE,
): MitgeltendesDokument[] => katalog.filter(d => d.art === 'C');

// ═══════════════════════════════════════════════════════════════════════════
// FILTER UND FORTSCHRITT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * relevant — erforderlich, zu klaeren, im SiGe-Plan oder schon angefasst
 * alle     — der ganze Katalog (Art A und B) samt eigenen Eintraegen
 * offen    — relevant und weder vorliegend noch als nicht erforderlich begruendet
 */
export type UnterlagenFilter = 'relevant' | 'alle' | 'offen';

export const istUnerledigt = (s: UnterlagenStatus): boolean => s === 'offen' || s === 'angefordert';

export const filtereUnterlagen = (
    zeilen: readonly UnterlagenZeile[],
    filter: UnterlagenFilter,
    suche = '',
): UnterlagenZeile[] => {
    const s = normalisiere(suche);
    return (zeilen || []).filter(z => {
        if (filter === 'relevant' && !z.relevant) return false;
        if (filter === 'offen' && !(z.relevant && istUnerledigt(z.status))) return false;
        if (!s) return true;
        const heu = [
            z.name,
            z.dokument?.kurz || '',
            ...(z.dokument?.alias || []),
            z.eintrag?.bemerkung || '',
        ].map(t => normalisiere(t)).join(' | ');
        return heu.includes(s);
    });
};

export interface UnterlagenFortschritt {
    /** Relevante Unterlagen ohne die als nicht erforderlich begruendeten. */
    relevant: number;
    liegtVor: number;
    angefordert: number;
    offen: number;
    nichtErforderlich: number;
    /** Erforderliche Unterlagen, die noch nicht einmal angefordert sind. */
    erforderlichOffen: UnterlagenZeile[];
}

/**
 * Kennzahlen fuer den Kopf.
 *
 * Der Nenner laesst „nicht erforderlich" aussen vor — wie die Klaerungsquote
 * der Fragen die gegenstandslosen: sie sind erledigt, ohne vorzuliegen, und
 * wuerden den Fortschritt sonst dauerhaft deckeln.
 */
export const berechneUnterlagenFortschritt = (zeilen: readonly UnterlagenZeile[]): UnterlagenFortschritt => {
    const rel = (zeilen || []).filter(z => z.relevant);
    const zaehle = (s: UnterlagenStatus) => rel.filter(z => z.status === s).length;
    const nichtErforderlich = zaehle('nicht_erforderlich');
    return {
        relevant: rel.length - nichtErforderlich,
        liegtVor: zaehle('liegt_vor'),
        angefordert: zaehle('angefordert'),
        offen: zaehle('offen'),
        nichtErforderlich,
        erforderlichOffen: rel.filter(z => z.erforderlich && z.status === 'offen'),
    };
};

export const fortschrittText = (f: UnterlagenFortschritt): string =>
    `${f.liegtVor} von ${f.relevant} relevanten Unterlagen liegen vor, ${f.angefordert} angefordert, ${f.offen} offen`;

/** Klartext, warum eine Zeile relevant ist — fuer die Kennzeichnung je Zeile. */
export const relevanzGruende = (z: UnterlagenZeile): string[] => {
    const gruende: string[] = [];
    if (z.relevanz === 'erforderlich') {
        gruende.push(`Erforderlich nach den Projektmerkmalen: ${z.merkmale.map(merkmalText).join(', ')}`);
    } else if (z.relevanz === 'zu_klaeren') {
        gruende.push(`Zu klären — Merkmal noch offen: ${z.merkmale.map(merkmalText).join(', ')}`);
    }
    if (z.sigeSchritte.length) {
        gruende.push(`Im SiGe-Plan genannt: ${z.sigeSchritte.join(', ')}`);
    }
    return gruende;
};

// ═══════════════════════════════════════════════════════════════════════════
// AENDERUNGEN — Statusuebergaenge, Bemerkung, Beleg, eigene Eintraege
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ergebnis einer Aenderung. Der Diskriminator ist eine ZEICHENKETTE — das
 * Projekt uebersetzt ohne `strictNullChecks`, ein boolean liesse sich dort
 * auf einem Objekt ohne den Schluessel versehentlich lesen (Muster
 * `planungIntegration.ts`).
 */
export type Aenderung<T> = { status: 'ok'; wert: T } | { status: 'fehler'; fehler: string };

const ok = <T>(wert: T): Aenderung<T> => ({ status: 'ok', wert });
const fehler = (f: string): { status: 'fehler'; fehler: string } => ({ status: 'fehler', fehler: f });

/** Der Eintrag zu einer Zeile — vorhanden oder frisch angelegt. Ein frischer
 *  Eintrag wird erst gespeichert, wenn die Aenderung durchgeht. */
export const eintragFuerZeile = (z: UnterlagenZeile): PlanungUnterlage =>
    z.eintrag
        ? { ...z.eintrag, lieferant: [...z.eintrag.lieferant] }
        : {
            id: z.id,
            quelle: z.quelle,
            name: z.name,
            status: 'offen',
            bemerkung: '',
            angefordert_am: null,
            status_am: null,
            dokument_id: null,
            lieferant: z.quelle === 'katalog' ? [] : [...z.lieferant],
        };

export const BEGRUENDUNG_FEHLT =
    'Bitte begründen Sie, warum die Unterlage nicht erforderlich ist — die Entscheidung wird dokumentiert.';

/**
 * Statuswechsel.
 *
 * - `angefordert`: stempelt `angefordert_am`, aber nur beim ersten Mal (b).
 * - `nicht_erforderlich`: verlangt eine Begruendung (c). Wird `begruendung`
 *   mitgegeben, ersetzt sie die Bemerkung; sonst muss die vorhandene
 *   Bemerkung tragen.
 * - Zurueck auf `offen` laesst `angefordert_am` stehen: dass angefordert
 *   wurde, bleibt wahr, auch wenn die Anforderung erneuert werden muss.
 * - Derselbe Status noch einmal aendert nichts, auch keinen Zeitstempel.
 * - `angefordert` mit `fristAm`: setzt die Rueckmeldefrist (Vorgabe des
 *   Aufrufers, `vorgabeUnterlagenFrist`). Anders als das Anforderungsdatum
 *   wird sie bei jeder neuen Anforderung ERSETZT — eine erneuerte Anforderung
 *   laeuft mit neuer Frist, das erste Datum bleibt davon unberuehrt. Ohne
 *   `fristAm` bleibt eine vorhandene Frist stehen; bei allen anderen Staenden
 *   bleibt sie als Akte erhalten und wird nur nicht mehr angezeigt.
 */
export const setzeUnterlagenStatus = (
    e: PlanungUnterlage,
    neu: UnterlagenStatus,
    opt: { jetztIso: string; begruendung?: string; fristAm?: string | null },
): Aenderung<PlanungUnterlage> => {
    if (!STATUS_MENGE.has(neu)) return fehler(`„${String(neu)}" ist kein gültiger Status.`);
    let bemerkung = e.bemerkung || '';
    if (neu === 'nicht_erforderlich') {
        const b = opt.begruendung !== undefined ? String(opt.begruendung).trim() : bemerkung.trim();
        if (!b) return fehler(BEGRUENDUNG_FEHLT);
        if (opt.begruendung !== undefined) bemerkung = b;
    }
    if (neu === e.status) {
        return ok(bemerkung === e.bemerkung ? e : { ...e, bemerkung });
    }
    // Das Feld wird nur angefasst, wenn eine Frist mitkommt — sonst behaelt
    // der Eintrag seine Gestalt (Altbestand ohne `frist_am` bleibt ohne).
    let frist: Partial<Pick<PlanungUnterlage, 'frist_am'>> = {};
    if (neu === 'angefordert' && opt.fristAm !== undefined) {
        const tag = isoTagOderNull(opt.fristAm);
        if (opt.fristAm && !tag) return fehler('Die Frist ist kein gültiges Datum (JJJJ-MM-TT).');
        frist = { frist_am: tag };
    }
    return ok({
        ...e,
        status: neu,
        bemerkung,
        angefordert_am: neu === 'angefordert' ? (e.angefordert_am || opt.jetztIso) : e.angefordert_am,
        status_am: opt.jetztIso,
        ...frist,
    });
};

/**
 * Rueckmeldefrist aendern oder (`null`/leer) loeschen — nur solange die
 * Unterlage unerledigt ist; bei „liegt vor" und „nicht erforderlich" gibt es
 * nichts mehr, das befristet waere.
 */
export const setzeUnterlagenFrist = (e: PlanungUnterlage, fristIso: string | null): Aenderung<PlanungUnterlage> => {
    if (!(e.status === 'offen' || e.status === 'angefordert')) {
        return fehler('Eine Frist gibt es nur für offene oder angeforderte Unterlagen.');
    }
    const roh = String(fristIso || '').trim();
    const tag = roh ? isoTagOderNull(roh) : null;
    if (roh && !tag) return fehler('Die Frist ist kein gültiges Datum (JJJJ-MM-TT).');
    return ok(tag === (e.frist_am ?? null) ? e : { ...e, frist_am: tag });
};

/** Bemerkung aendern. Bei `nicht_erforderlich` darf sie nicht leer werden (c). */
export const setzeUnterlagenBemerkung = (e: PlanungUnterlage, textNeu: string): Aenderung<PlanungUnterlage> => {
    const t = String(textNeu ?? '');
    if (e.status === 'nicht_erforderlich' && !t.trim()) {
        return fehler('Die Begründung lässt sich nicht leeren, solange die Unterlage als „nicht erforderlich" gilt. Ändern Sie zuerst den Status.');
    }
    return ok(t === e.bemerkung ? e : { ...e, bemerkung: t });
};

/**
 * Beleg aus der Projektablage verknuepfen oder loesen.
 *
 * Ein gesetzter Verweis heisst „liegt vor" — dieselbe Regel wie in der
 * Anlagenliste des A+S-Plans (`asplan/ablage/anlagen.ts`): das Dokument IN
 * der Ablage ist der Nachweis. Deshalb hebt das Verknuepfen den Status auf
 * `liegt_vor`. Das Loesen laesst den Status stehen: ob die Unterlage
 * trotzdem vorliegt (etwa nur auf Papier), weiss nur der Nutzer.
 */
export const verknuepfeUnterlagenDokument = (
    e: PlanungUnterlage,
    dokumentId: string | null,
    jetztIso: string,
): PlanungUnterlage => {
    const id = dokumentId && dokumentId.trim() ? dokumentId.trim() : null;
    if (!id) return e.dokument_id ? { ...e, dokument_id: null } : e;
    if (e.status === 'liegt_vor') return id === e.dokument_id ? e : { ...e, dokument_id: id };
    return { ...e, dokument_id: id, status: 'liegt_vor', status_am: jetztIso };
};

/** Ersetzt den Eintrag gleicher Kennung oder haengt ihn an. Entfernt nie. */
export const uebernehmeUnterlage = (
    liste: readonly PlanungUnterlage[],
    e: PlanungUnterlage,
): PlanungUnterlage[] => {
    const alt = liste || [];
    return alt.some(x => x.id === e.id) ? alt.map(x => (x.id === e.id ? e : x)) : [...alt, e];
};

/**
 * Eine freie, eigene Unterlage.
 *
 * Abgelehnt wird, was schon da ist: ein Katalogdokument (dann bitte dort den
 * Status setzen — sonst stuende dieselbe Unterlage zweimal in der Akte, mit
 * zwei verschiedenen Staenden) und ein eigener Eintrag gleichen Namens.
 * Geprueft wird gegen den VOLLEN Katalog einschliesslich Art C: eine
 * Betriebsanleitung oder Gefaehrdungsbeurteilung als eigene Zeile hebelte
 * den Grundsatz aus, nach dem Art C nie je Zeile erscheint.
 * Die Kennung gibt der Aufrufer vor (Zufall gehoert nicht in eine pure
 * Funktion).
 */
export const neueEigeneUnterlage = (
    name: string,
    lieferant: FrageAdressat[],
    kennung: string,
    vorhandene: readonly PlanungUnterlage[],
    katalog: readonly MitgeltendesDokument[] = DOKUMENTE,
): Aenderung<PlanungUnterlage> => {
    const n = String(name || '').trim();
    if (!n) return fehler('Bitte einen Namen für die Unterlage angeben.');
    const imKatalog = sucheImKatalog(n, katalog);
    if (imKatalog && imKatalog.art === 'C') {
        return fehler(`„${imKatalog.name}" ist eine allgemeine Pflicht des Arbeitgebers. Sie gilt für jeden Arbeitgeber über den Grundsatz unter der Checkliste und wird nicht als einzelne Unterlage geführt oder angefordert.`);
    }
    if (imKatalog) return fehler(`„${imKatalog.name}" steht bereits im Katalog — bitte dort den Status setzen.`);
    const norm = normalisiere(n);
    const doppelt = (vorhandene || []).find(e => normalisiere(e.name) === norm);
    if (doppelt) return fehler(`„${doppelt.name}" steht bereits in der Checkliste.`);
    const k = String(kennung || '').trim();
    if (!k) return fehler('Interner Fehler: keine Kennung für den Eintrag.');
    return ok({
        id: `${PRAEFIX_EIGEN}${TRENNER}${k}`,
        quelle: 'eigen',
        name: n,
        status: 'offen',
        bemerkung: '',
        angefordert_am: null,
        status_am: null,
        dokument_id: null,
        lieferant: (lieferant || []).filter(a => ADRESSATEN.has(a)),
    });
};

/**
 * Eine eigene Unterlage loeschen — die einzige Stelle, an der ein Eintrag
 * verschwindet, und nur auf ausdrueckliche Handlung.
 *
 * Erlaubt nur, solange sie nichts belegt: eigen, nie angefordert, ohne
 * Beleg, Status offen. Alles andere ist Akte; wer es loswerden will, setzt
 * es auf „nicht erforderlich" und begruendet das.
 */
export const entferneEigeneUnterlage = (
    liste: readonly PlanungUnterlage[],
    id: string,
): Aenderung<PlanungUnterlage[]> => {
    const e = (liste || []).find(x => x.id === id);
    if (!e) return fehler('Den Eintrag gibt es nicht mehr.');
    if (e.quelle !== 'eigen') return fehler('Nur eigene Unterlagen lassen sich löschen.');
    if (e.status !== 'offen' || e.angefordert_am || e.dokument_id) {
        return fehler('Diese Unterlage wurde bereits angefordert oder belegt und bleibt deshalb in der Akte. Setzen Sie sie bei Bedarf auf „nicht erforderlich" und begründen Sie das.');
    }
    return ok((liste || []).filter(x => x.id !== id));
};

/**
 * Meldung, wenn das Speichern scheitert.
 *
 * Der haeufigste Grund nach einem Deploy ist eine noch nicht gefahrene
 * Migration: dann kennt PostgREST die Spalte nicht (`PGRST204`, bzw. `42703`
 * aus Postgres). Das bekommt einen eigenen Satz — „Could not find the
 * 'unterlagen' column" hilft niemandem auf der Baustelle weiter.
 */
export const unterlagenSpeicherFehler = (roh: string): string => {
    const r = String(roh || '').trim().replace(/[.\s]+$/, '');
    const spalteFehlt = /PGRST204|42703/.test(r) && /unterlagen/i.test(r);
    if (spalteFehlt) {
        return 'Die Checkliste wurde nicht gespeichert: Die Datenbank kennt die Spalte „unterlagen" noch nicht (Migration Nr. 156). Ihr Stand bleibt hier erhalten — bitte den Administrator verständigen.';
    }
    return `Die Checkliste wurde nicht gespeichert: ${r || 'unbekannter Fehler'}. Ihr Stand bleibt hier erhalten.`;
};

// ═══════════════════════════════════════════════════════════════════════════
// BELEGE AUS DER PROJEKTABLAGE
// ═══════════════════════════════════════════════════════════════════════════

/** Was die Checkliste von einem Dokument der Projektablage wissen muss. */
export interface AblageDokumentKurz {
    id: string;
    titel: string;
    dateiname?: string;
    nur_admin?: boolean;
    module?: string[];
}

/**
 * Die Dokumente, die als Beleg taugen.
 *
 * Admin-gesperrte fallen weg — dieselbe Regel wie `verknuepfbareDokumente`
 * im A+S-Plan: fuer Nicht-Admins existieren sie nicht, ein Verweis darauf
 * belegte fuer die meisten Leser nichts. Dem Modul Planungsphase zugeordnete
 * Dokumente stehen vorn, danach alphabetisch.
 */
export const belegfaehigeDokumente = <T extends AblageDokumentKurz>(dokumente: readonly T[]): T[] =>
    (dokumente || [])
        .filter(d => d && d.nur_admin !== true)
        .sort((a, b) => {
            const pa = (a.module || []).includes('planungsphase') ? 0 : 1;
            const pb = (b.module || []).includes('planungsphase') ? 0 : 1;
            if (pa !== pb) return pa - pb;
            return (a.titel || a.dateiname || '').localeCompare(b.titel || b.dateiname || '', 'de');
        });

/** Verweist der Eintrag auf ein Dokument, das die Ablage nicht (mehr) als
 *  Beleg liefert — geloescht oder nachtraeglich admin-gesperrt? */
export const belegVerwaist = (
    e: PlanungUnterlage | null,
    dokumente: readonly AblageDokumentKurz[],
): boolean => {
    if (!e || !e.dokument_id) return false;
    return !belegfaehigeDokumente(dokumente).some(d => d.id === e.dokument_id);
};

// ═══════════════════════════════════════════════════════════════════════════
// ANFORDERUNG JE LIEFERANT
// ═══════════════════════════════════════════════════════════════════════════

/** Unerledigte relevante Unterlagen, die ein bestimmter Lieferant liefert. */
export const unterlagenFuerLieferant = (
    zeilen: readonly UnterlagenZeile[],
    lieferant: FrageAdressat,
): UnterlagenZeile[] =>
    (zeilen || []).filter(z => z.relevant && istUnerledigt(z.status) && z.lieferant.includes(lieferant));

/**
 * Nach dem Versand einer Anforderung: die angeforderten Unterlagen als
 * „angefordert" festhalten.
 *
 * Bewusst eine ausdrueckliche Handlung nach dem Export und kein Nebeneffekt
 * des PDF — wie „Als gestellt markieren" bei den Fragen: ein erzeugtes Blatt
 * ist noch nicht verschickt. Gestempelt werden nur Unterlagen, die noch
 * „offen" sind; eine bereits angeforderte behaelt ihr ERSTES Datum (b), und
 * eine vorliegende oder als nicht erforderlich begruendete wird nicht
 * zurueckgestuft. Grundlage ist der aktuelle Stand in `liste`, nicht der
 * Stand beim Zeichnen der Zeilen. `fristAm` (Vorgabe heute + 14 Tage) wird
 * jeder gestempelten Unterlage als Rueckmeldefrist mitgegeben.
 */
export const markiereAlsAngefordert = (
    liste: readonly PlanungUnterlage[],
    zeilen: readonly UnterlagenZeile[],
    jetztIso: string,
    fristAm?: string | null,
): { liste: PlanungUnterlage[]; gestempelt: number } => {
    let neu: PlanungUnterlage[] = [...(liste || [])];
    let gestempelt = 0;
    (zeilen || []).forEach(z => {
        const vorhanden = neu.find(e => e.id === z.id);
        const basis = vorhanden ? { ...vorhanden } : eintragFuerZeile({ ...z, eintrag: null });
        if (basis.status !== 'offen') return;
        const r = setzeUnterlagenStatus(basis, 'angefordert', { jetztIso, fristAm });
        if (r.status !== 'ok') return;
        neu = uebernehmeUnterlage(neu, r.wert);
        gestempelt += 1;
    });
    return { liste: neu, gestempelt };
};

// ═══════════════════════════════════════════════════════════════════════════
// VERSAND DER ANFORDERUNG — Vorbelegung, Empfaenger, Stempel erst nach Erfolg
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Hausregel (Plan 4.1): ERST der Mail-Erfolg stempelt „angefordert". Der
 * Versand selbst (Ablage + Mail) liegt in `planungVersand.ts`; hier steht
 * die Reihenfolge drumherum, pruefbar ohne Netz und ohne React — der Versand
 * und der Stempel werden hereingereicht.
 *
 * Drei Ausgaenge, als Zeichenketten unterscheidbar (Muster `Aenderung`):
 *   fehler         — die Mail ging nicht raus; NICHTS wurde gestempelt.
 *   versendet      — Mail raus, Unterlagen als angefordert festgehalten.
 *   stempel_offen  — Mail raus, aber der Stempel ging nicht (Checkliste
 *                    gesperrt, Phase weg …). Das muss sichtbar bleiben, mit
 *                    „Stempel nachholen" — sonst steht eine verschickte
 *                    Anforderung ohne Datum in der Akte.
 * `teilweise` (nicht jeder Empfaenger erreicht) zaehlt als Erfolg: die
 * Anforderung IST bei mindestens einer Person angekommen, und das erste
 * Datum zaehlt. Wer nicht erreicht wurde, steht in `fehler` und wird
 * gemeldet.
 */

/** Was der Versand zurueckmeldet — der Ausschnitt aus `VersandErgebnis`
 *  (`planungVersand.ts`), den die Reihenfolge braucht. */
export interface VersandRueckmeldung {
    status: 'versendet' | 'teilweise' | 'fehler';
    /** Je nicht erreichter Empfaenger ein Satz. */
    fehler?: unknown;
    /** Zeitpunkt des ERSTEN Erfolgs — er wird gestempelt, nicht der Klick. */
    versendetAm?: string | null;
    /** Nicht-fatale Hinweise (Ablage gescheitert, Sendebericht fehlt) — werden gemeldet, nie verschluckt. */
    hinweise?: unknown;
    /** Hat die Mail den ERSTEN Empfaenger (den Adressaten der Anforderung)
     *  erreicht? Nur dann ist angefordert. Fehlt das Feld, zaehlt `status`. */
    hauptempfaengerErreicht?: boolean;
}

/**
 * Was der Stempel zurueckmeldet. „Gestempelt" heisst: der Stand steht in der
 * DATENBANK — nicht nur in der Oberflaeche eingereiht (`AenderungsQuittung`).
 *   gesperrt          — die Checkliste durfte nicht schreiben (Lesemodus, Phase weg)
 *   nicht_gespeichert — eingereiht, aber die Datenbank hat nicht bestaetigt
 */
export type StempelErgebnis =
    | { status: 'gestempelt'; gestempelt: number }
    | { status: 'gesperrt'; grund: string }
    | { status: 'nicht_gespeichert'; grund: string };

export type AnforderungVersandErgebnis =
    | { status: 'fehler'; fehler: string }
    | { status: 'versendet'; gestempelt: number; teilweise: boolean; versendetAm: string; fehler: string | null; hinweise: string | null }
    | { status: 'stempel_offen'; grund: string; teilweise: boolean; versendetAm: string; fehler: string | null; hinweise: string | null };

/** Fehlertext aus dem, was der Versand liefert — Zeichenkette, Liste, Error oder nichts. */
export const versandFehlerText = (f: unknown): string | null => {
    if (f == null) return null;
    if (typeof f === 'string') return f.trim() || null;
    if (Array.isArray(f)) {
        const teile = f.map(versandFehlerText).filter((t): t is string => !!t);
        return teile.length ? teile.join(' · ') : null;
    }
    if (f instanceof Error) return f.message || null;
    if (typeof f === 'object') {
        const o = f as Record<string, unknown>;
        const t = typeof o.message === 'string' ? o.message : typeof o.fehler === 'string' ? o.fehler : '';
        return t.trim() || null;
    }
    return String(f);
};

export const fuehreAnforderungVersandAus = async (opt: {
    versende: () => Promise<VersandRueckmeldung>;
    /** Stempelt gegen den aktuellen Stand mit dem Versandzeitpunkt und
     *  wartet auf die Datenbankbestaetigung. */
    stempeln: (versendetAm: string) => Promise<StempelErgebnis>;
    /** Rueckfall fuer den Stempel, wenn der Versand keinen Zeitpunkt nennt. */
    jetztIso: string;
}): Promise<AnforderungVersandErgebnis> => {
    let r: VersandRueckmeldung;
    try {
        r = await opt.versende();
    } catch (e) {
        return { status: 'fehler', fehler: versandFehlerText(e) || 'Der Versand ist gescheitert.' };
    }
    if (!r || r.status === 'fehler') {
        return { status: 'fehler', fehler: versandFehlerText(r && r.fehler) || 'Die Anforderung wurde nicht versendet.' };
    }
    const fehlerText = versandFehlerText(r.fehler);
    // Angefordert ist nur, was den Adressaten erreicht hat — eine Kopie an
    // Dritte stempelt nichts.
    if (r.hauptempfaengerErreicht === false) {
        return {
            status: 'fehler',
            fehler: `Der Empfänger der Anforderung wurde nicht erreicht${fehlerText ? `: ${fehlerText}` : '.'}`,
        };
    }
    const teilweise = r.status === 'teilweise';
    const hinweise = versandFehlerText(r.hinweise);
    const versendetAm = typeof r.versendetAm === 'string' && r.versendetAm ? r.versendetAm : opt.jetztIso;
    let s: StempelErgebnis;
    try {
        s = await opt.stempeln(versendetAm);
    } catch (e) {
        s = { status: 'nicht_gespeichert', grund: versandFehlerText(e) || 'Die Checkliste wurde nicht gespeichert.' };
    }
    if (s.status !== 'gestempelt') {
        return { status: 'stempel_offen', grund: s.grund, teilweise, versendetAm, fehler: fehlerText, hinweise };
    }
    return { status: 'versendet', gestempelt: s.gestempelt, teilweise, versendetAm, fehler: fehlerText, hinweise };
};

/** Empfaenger einer Anforderung — Name und E-Mail sind beide Pflicht, anders
 *  als in der Zuordnung (dort reicht der Name): ohne Adresse keine Mail. */
export interface AnforderungsEmpfaenger {
    name: string;
    email: string;
}

/** Vorbelegung des Empfaengers aus der Zuordnung Adressat → Person; was
 *  fehlt, bleibt leer und wird eingegeben. */
export const empfaengerVorbelegung = (
    zuordnung: Partial<Record<FrageAdressat, { name: string; email?: string }>> | null | undefined,
    lieferant: FrageAdressat,
): AnforderungsEmpfaenger => {
    const e = zuordnung ? zuordnung[lieferant] : undefined;
    return { name: e && e.name ? e.name.trim() : '', email: e && e.email ? e.email.trim() : '' };
};

/** Was an der Empfaengereingabe nicht stimmt — oder `null`. */
export const pruefeAnforderungsEmpfaenger = (e: AnforderungsEmpfaenger): string | null => {
    if (!String(e.name || '').trim()) return 'Bitte den Namen des Empfängers eintragen.';
    const mail = String(e.email || '').trim();
    if (!mail) return 'Bitte die E-Mail-Adresse des Empfängers eintragen — ohne Adresse lässt sich nichts versenden.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) return 'Die E-Mail-Adresse ist nicht vollständig.';
    return null;
};

/** Betreff der Anforderungsmail. */
export const anforderungBetreff = (projektName?: string, projektNummer?: string): string => {
    const name = String(projektName || '').trim();
    const nr = String(projektNummer || '').trim();
    const bezug = name && nr ? `${name} (Projekt-Nr. ${nr})` : name || (nr ? `Projekt-Nr. ${nr}` : '');
    return bezug ? `Anforderung von Unterlagen – ${bezug}` : 'Anforderung von Unterlagen';
};

/**
 * Vorbelegter Mailtext. Nennt die Unterlagen und die Rueckmeldefrist; die
 * Einzelheiten (Zweck, Vorlauf, Rechtsgrundlage) stehen auf dem Blatt im
 * Anhang. Interne Bemerkungen gehoeren nicht hinein — wie beim Blatt.
 */
export const anforderungMailText = (opt: {
    empfaengerName: string;
    projektName?: string;
    projektNummer?: string;
    unterlagen: readonly string[];
    /** ISO-Tag der Rueckmeldefrist (`vorgabeUnterlagenFrist`). */
    fristIso: string;
    absender?: string;
}): string => {
    const name = String(opt.projektName || '').trim();
    const nr = String(opt.projektNummer || '').trim();
    const bezug = name ? `zum Bauvorhaben „${name}"${nr ? ` (Projekt-Nr. ${nr})` : ''}` : (nr ? `zum Projekt Nr. ${nr}` : 'zu dem oben genannten Bauvorhaben');
    const liste = (opt.unterlagen || []).map(u => `- ${u}`).join('\n');
    const einzahl = (opt.unterlagen || []).length === 1;
    const frist = datumDeutsch(opt.fristIso);
    const anrede = String(opt.empfaengerName || '').trim();
    return [
        anrede ? `Sehr geehrte/r ${anrede},` : 'Sehr geehrte Damen und Herren,',
        '',
        `für die Koordination nach Baustellenverordnung benötigen wir ${bezug} ${einzahl ? 'die folgende Unterlage' : 'die folgenden Unterlagen'}:`,
        '',
        liste,
        '',
        'Zweck, Vorlauf und Rechtsgrundlage je Unterlage entnehmen Sie bitte dem beigefügten Anforderungsblatt.'
            + (frist ? ` Wir bitten um Rückmeldung bis ${frist}.` : ''),
        'Liegt eine Unterlage aus Ihrer Sicht nicht vor oder ist sie nicht erforderlich, bitten wir um einen kurzen Hinweis mit Begründung.',
        '',
        'Mit freundlichen Grüßen',
        String(opt.absender || '').trim(),
    ].join('\n').replace(/\n+$/, '');
};

/** Kennung der versendeten Fassung — je Lieferant und Zeitpunkt eindeutig,
 *  damit die Ablage (Unique-Index `erzeugt_aus` + `fassung_ref`) zwei
 *  Anforderungen am selben Tag nicht als Dublette verwirft. */
export const anforderungFassungRef = (lieferant: FrageAdressat, jetztIso: string): string =>
    `unterlagenanforderung:${lieferant}:${String(jetztIso || '').replace(/[^\dT]/g, '').slice(0, 15) || 'ohne-zeit'}`;

/** Unerledigte Zeilen, deren Rueckmeldefrist verstrichen ist — fuer den
 *  Hinweis im Kopf; Ampel und Nachtlauf lesen `frist_am` spaeter selbst. */
export const ueberfaelligeUnterlagen = (
    zeilen: readonly UnterlagenZeile[],
    heuteIso: string,
): UnterlagenZeile[] =>
    (zeilen || []).filter(z => unterlagenFristUeberfaellig(z.eintrag, heuteIso));

/** Lieferanten mit Zahl unerledigter relevanter Unterlagen — die Knopfleiste
 *  fuer die Anforderung. Meiste zuerst. */
export const lieferantenUebersicht = (
    zeilen: readonly UnterlagenZeile[],
): { lieferant: FrageAdressat; offen: number }[] => {
    const map = new Map<FrageAdressat, number>();
    (zeilen || []).forEach(z => {
        if (!z.relevant || !istUnerledigt(z.status)) return;
        z.lieferant.forEach(l => map.set(l, (map.get(l) || 0) + 1));
    });
    return Array.from(map.entries())
        .map(([lieferant, offen]) => ({ lieferant, offen }))
        .sort((a, b) => b.offen - a.offen || a.lieferant.localeCompare(b.lieferant));
};
